import {
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress
} from '@mui/material';
import { Box } from '@mui/system';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// react icons
import { FaRegUser } from 'react-icons/fa';
import { FiCheck, FiX } from 'react-icons/fi';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { GiMoneyStack, GiPayMoney, GiTakeMyMoney } from 'react-icons/gi';
import { HiReceiptTax } from 'react-icons/hi';
import { MdOutlineDateRange, MdOutlineMoreHoriz, MdOutlineSendToMobile } from 'react-icons/md';
import { TbReceiptTax } from 'react-icons/tb';
import { BiCloudUpload } from 'react-icons/bi';
// material icons
import AddCardIcon from '@mui/icons-material/AddCard';
import AdUnitsIcon from '@mui/icons-material/AdUnits';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import EventNoteIcon from '@mui/icons-material/EventNote';
import GroupIcon from '@mui/icons-material/Group';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { fDate } from '../../utils/formatTime';
import { NOT_AVAILABLE } from '../Global/Constants';
// componenets
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import usePayoutSelector from '../../store/Slice/Payout/PayoutSelector';
import {
  getBankDropdownData,
  getCityData,
  getLoanTypeData,
  saveApiData,
  savefileApiData,
  downloadApiData
} from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import {
  getAdvancePendingViewList,
  getPayoutCommentLogs,
  getPayoutViewData
} from '../../store/Slice/Payout/PayoutSlice';
import { getRevenuePartnerDetails } from '../../store/Slice/Revenue/RevenueSlice';
import ViewCardComponent from '../../components/ViewCards';
import {
  normalizeValue,
  formatDateToDDMMYYYY,
  formatDateToYYYYMMDD
} from '../../utils/CommonFunction';
import { StyledIconButton, StyledTableRow } from '../../utils/StyleTable';
import { PayoutApi, PayableApi } from '../../utils/Apis';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect';
import { setApiError, setApiSuccess } from '../../store/Slice/Error/ErrorSlice';
import { resetErrorSlice } from '../../store/Actions';
import { UserMoreMenu } from '../../components/_dashboard/user';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 12,
    fontWeight: '700',
    padding: '12px 10px',
    color: '#637381',
    textTransform: 'uppercase',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 10px',
    borderBottom: '1px solid #f4f6f8'
  }
}));

export const addPayoutValidationSchema = yup.object().shape({
  fileNumber: yup
    .string()
    .required('Please add file no')
    .max(50, 'File No must be less than 50 characters'),

  fileSubmissionDate: yup.string().required('Please add submission date'),

  bankId: yup
    .string()
    .nullable()
    .required('Bank Name is required')
    .notOneOf(['', ' ', null], 'Please select a valid Bank'),
  cityId: yup
    .string()
    .nullable()
    .required('City Name is required')
    .notOneOf(['', ' ', null], 'Please select a valid City'),

  borrowerName: yup
    .string()
    .required('Please add borrower name')
    .max(50, 'Borrower Name must be less than 50 characters'),

  loanTypeId: yup
    .string()
    .required('Please select loan type')
    .notOneOf(['', ' ', null], 'Please select a valid Loan Type'),

  disburseDate: yup.string().required('Please add disbursed date'),

  disburseAmount: yup
    .number()
    .typeError('Please add disbursed amount')
    .required('Please add disbursed amount')
    .positive('Disbursed Amount must be greater than 0')
    .max(10000000000, 'Please add disbursed amount less than 10,00,00,00,000'),

  sanctionedDate: yup.string().required('Please add sanctioned date'),

  sanctionedAmount: yup
    .number()
    .typeError('Please add sanctioned amount')
    .required('Please add sanctioned amount')
    .positive('Sanctioned Amount must be greater than 0')
    .max(10000000000, 'Please add sanctioned amount less than 10,00,00,00,000'),

  encashmentDate: yup.string().required('Please add encashment date'),

  encashmentAmount: yup
    .number()
    .typeError('Please add encashment amount')
    .required('Please add encashment amount')
    .positive('Encashment Amount must be greater than 0')
    .max(10000000000, 'Please add encashment amount less than 10,00,00,00,000')
});

function ViewPayout() {
  const [openSendMsg, setOpenSendMsg] = useState(false);
  const [editFile, setEditFile] = useState(false);
  const [fullWidth, setFullWidth] = useState(true);
  const [maxWidth, setMaxWidth] = useState('md');
  const [viewMorePopup, setViewMorePopup] = useState(false);
  const [addNewComment, setAddNewComment] = useState('');
  const [completeCommentMessage, setCompleteCommentMessage] = useState('');
  const fileInputRef = useRef(null);
  const [sanctionedFile, setSanctionedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [invoicePdfUrl, setInvoicePdfUrl] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [confirmRejectDialog, setConfirmRejectDialog] = useState(false);
  const [confirmRejectAction, setConfirmRejectAction] = useState('CONFIRM');
  const [invoiceActionStatus, setInvoiceActionStatus] = useState(null);
  const [previousPayoutId, setPreviousPayoutId] = useState(null);

  const {
    handleSubmit: handleReduxSubmit,
    control,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    mode: 'onBlur', // Add this to validate on blur
    defaultValues: {
      fileNumber: '',
      fileSubmissionDate: '',
      bankId: '',
      cityId: '',
      borrowerName: '',
      loanTypeId: '',
      disburseDate: '',
      disburseAmount: '',
      sanctionedDate: '',
      sanctionedAmount: '',
      encashmentDate: '',
      encashmentAmount: ''
    }
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { rowId } = location?.state || {};
  // console.log('sss rowId data', rowId);
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  // console.log('LoginData in payout view page', LoginData);
  const { PayoutCommentLog, PayoutViewData, AdvancePendingViewData } = usePayoutSelector();
  const { LoanTypeDrop, BankDrop, CityDrop } = useGlobalSelector();

  // console.log('PayoutViewData222', PayoutViewData);
  // console.log('AdvancePendingViewData', AdvancePendingViewData);

  const handleEditOpen = (e) => {
    setEditFile(true);
    reset();
    setValue('fileNumber', PayoutViewData?.fileNumber || '');
    setValue('fileSubmissionDate', formatDateToYYYYMMDD(PayoutViewData?.fileSubmissionDate) || '');
    setValue('bankId', PayoutViewData?.bankId || null);
    setValue('cityId', PayoutViewData?.cityId || null);
    setValue('borrowerName', PayoutViewData?.borrowerName || '');
    setValue('loanTypeId', PayoutViewData?.loanTypeId || '');
    setValue(
      'disburseDate',
      formatDateToYYYYMMDD(PayoutViewData?.disburseDate || PayoutViewData?.disburseMonth) || ''
    );
    setValue('disburseAmount', PayoutViewData?.disburseAmount || '');
    setValue(
      'sanctionedDate',
      formatDateToYYYYMMDD(PayoutViewData?.sanctionedDate || PayoutViewData?.sanctionedMonth) || ''
    );
    setValue('sanctionedAmount', PayoutViewData?.sanctionedAmount || '');
    setValue(
      'encashmentDate',
      formatDateToYYYYMMDD(PayoutViewData?.encashmentDate || PayoutViewData?.encashmentMonth) || ''
    );
    setValue('encashmentAmount', PayoutViewData?.encashmentAmount || '');
  };

  const onSubmit = useCallback(
    async (data) => {
      const finalData = {
        ...data,
        disburseDate: formatDateToDDMMYYYY(data.disburseDate),
        sanctionedDate: formatDateToDDMMYYYY(data.sanctionedDate),
        encashmentDate: formatDateToDDMMYYYY(data.encashmentDate),
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        employeeClass: 'Others',
        partnerId: null,
        applicationId: rowId?.applicationId ? rowId?.applicationId : null,
        partnerPayoutId: rowId?.partnerPayoutId ? rowId?.partnerPayoutId : null
      };
      try {
        const res = await dispatch(saveApiData(PayoutApi?.addupdatePayout, finalData));
        if (res?.status === 'success') {
          dispatch(setApiSuccess('Payout updated successfully'));
          getPayoutViewApi();
          setEditFile(false);
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        dispatch(setApiError('Something went wrong'));
      }
    },
    [LoginData] // 🔹 depends on LoginData
  );

  const handleSanctionedLetterUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSanctionedFile(file);
    setUploadingFile(true);
    setFileUploaded(false);
    try {
      const formData = new FormData();
      formData.append('partnerPayoutId', rowId?.partnerPayoutId || '');
      formData.append('companyId', LoginData?.companyId || '');
      formData.append('userId', LoginData?.userId || '');
      formData.append('file', file);
      const res = await dispatch(savefileApiData(PayoutApi?.uploadSanctionedLetter, formData));
      if (res) {
        dispatch(setApiSuccess('Sanctioned letter uploaded successfully'));
        setFileUploaded(true);
      }
    } catch (error) {
      console.error('Sanctioned letter upload error:', error);
      dispatch(setApiError('Something went wrong'));
    } finally {
      setUploadingFile(false);
    }
  };

  useEffect(() => {
    if (PayoutViewData?.partnerPayoutId) {
      dispatch(resetErrorSlice()); // 🔹 clear stale errors on row change
    }
  }, [PayoutViewData?.partnerPayoutId, dispatch]);

  useEffect(
    () => () => {
      if (invoicePdfUrl) {
        URL.revokeObjectURL(invoicePdfUrl);
      }
    },
    [invoicePdfUrl]
  );

  useEffect(() => {
    if (PayoutViewData) {
      const currentPayoutId = PayoutViewData?.partnerPayoutId;
      const isRowChanged = previousPayoutId !== null && previousPayoutId !== currentPayoutId;

      if (isRowChanged) {
        // 🔹 Only reset these states when switching to a different row
        setFileUploaded(false);
        setSanctionedFile(null);
        setInvoiceActionStatus(null);
      }

      setPreviousPayoutId(currentPayoutId);

      // Apply the status from backend if it is returned: invoiceStatus (PENDING, CONFIRMED, REJECTED, NOT_GENERATED)
      if (PayoutViewData?.invoiceStatus) {
        if (
          PayoutViewData.invoiceStatus === 'CONFIRMED' ||
          PayoutViewData.invoiceStatus === 'CONFIRM'
        ) {
          setInvoiceActionStatus('CONFIRMED');
        } else if (
          PayoutViewData.invoiceStatus === 'REJECTED' ||
          PayoutViewData.invoiceStatus === 'REJECT'
        ) {
          setInvoiceActionStatus('REJECTED');
        } else if (PayoutViewData.invoiceStatus === 'PENDING') {
          setInvoiceActionStatus('PENDING');
        } else {
          setInvoiceActionStatus(null);
        }
      } else if (isRowChanged) {
        // 🔹 Only reset to null on row change (keeps local state if we just clicked confirm on same row)
        setInvoiceActionStatus(null);
      }
    }
  }, [PayoutViewData, previousPayoutId]);

  const handleConfirmReject = async () => {
    const pId = PayoutViewData?.paybleId || PayoutViewData?.payableId || PayoutViewData?.invoiceId;

    if (!pId) return;
    const credentials = {
      userId: LoginData?.userId,
      paybleId: pId,
      action: confirmRejectAction
    };
    try {
      const result = await dispatch(saveApiData(PayableApi?.payableConfirmInvoice, credentials));
      if (result?.status === 'success' || result) {
        dispatch(
          setApiSuccess(
            confirmRejectAction === 'CONFIRM'
              ? 'Invoice confirmed successfully'
              : 'Invoice rejected successfully'
          )
        );
        setConfirmRejectDialog(false);
        setInvoiceActionStatus(confirmRejectAction === 'CONFIRM' ? 'CONFIRMED' : 'REJECTED');
        getPayoutViewApi(); // refresh view
      }
    } catch (error) {
      console.error('Confirm/Reject error:', error);
      dispatch(setApiError('Something went wrong'));
    }
  };

  const handleAddComment = async (e) => {
    if (addNewComment === '') {
      dispatch(setApiError('Please write your comments'));
      return;
    }

    const credentials = {
      comment: addNewComment,
      commentPutBy: LoginData?.userId,
      partnerPayoutId: rowId !== null ? rowId.partnerPayoutId : null
    };
    try {
      const res = await dispatch(saveApiData(PayoutApi?.payoutAddComment, credentials));
      if (res?.status === 'success') {
        dispatch(setApiSuccess('Comment added successfully'));
        getPayoutViewApi();
        dispatch(getPayoutCommentLogs(rowId?.partnerPayoutId));
        setOpenSendMsg(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      dispatch(setApiError('Something went wrong'));
    }
  };

  // loan type & Bank Dropdown
  const getLoanTypeBankDropAPi = useCallback(() => {
    if (!LoginData) return;
    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getLoanTypeData(data));
    dispatch(getBankDropdownData(data));
  }, [LoginData]);

  // payout comment log
  const getPayoutCommentLogApi = useCallback(() => {
    if (!rowId) return;
    dispatch(getPayoutCommentLogs(rowId?.partnerPayoutId));
  }, [rowId]);

  // payout view
  const getPayoutViewApi = useCallback(() => {
    if (!rowId || !LoginData) return;
    const credentials = {
      applicationId: rowId?.applicationId,
      companyId: LoginData?.companyId,
      partnerPayoutId: rowId?.partnerPayoutId,
      userId: LoginData?.userId
    };
    dispatch(getPayoutViewData(credentials));
  }, [rowId, LoginData]);

  const getAdvancePendingViewApi = useCallback(() => {
    if (!rowId || !LoginData) return;
    const credentials = {
      payoutId: rowId?.partnerPayoutId,
      userId: LoginData?.userId
    };
    dispatch(getAdvancePendingViewList(credentials));
  }, [rowId, LoginData]);

  // city api
  const handleGetState = (optionVal) => {
    const data = {
      stateId: optionVal
    };
    dispatch(getCityData(data)); // city data
  };

  useEffect(() => {
    dispatch(resetErrorSlice()); // 🔹 clear stale errors on load/row change
    handleGetState(null); // city api
    dispatch(getRevenuePartnerDetails()); // partner details
  }, []);

  useEffect(() => {
    getPayoutCommentLogApi();
  }, [getPayoutCommentLogApi]);

  useEffect(() => {
    getLoanTypeBankDropAPi();
  }, [getLoanTypeBankDropAPi]);

  useEffect(() => {
    getPayoutViewApi();
  }, [getPayoutViewApi]);

  useEffect(() => {
    getAdvancePendingViewApi();
  }, [getAdvancePendingViewApi]);

  // ✅ Commission Details fields (memoized)
  const commissionDetailsFields = useMemo(
    () => [
      {
        icon: <GiTakeMyMoney className="viewLabelIcon" size={24} />,
        label: 'Amount Sanctioned',
        value: normalizeValue(PayoutViewData?.sanctionedAmount, NOT_AVAILABLE)
      },
      {
        icon: <GiPayMoney className="viewLabelIcon" size={24} />,
        label: 'Amount Disbursed',
        value: normalizeValue(PayoutViewData?.disburseAmount, NOT_AVAILABLE)
      },
      {
        icon: <AdUnitsIcon className="viewLabelIcon" size={24} />,
        label: 'Platform Commission',
        value: normalizeValue(PayoutViewData?.platformCommission, NOT_AVAILABLE)
      },
      {
        icon: <GroupIcon className="viewLabelIcon" size={24} />,
        label: 'Partner Commission',
        value: normalizeValue(PayoutViewData?.partnerCommission, NOT_AVAILABLE)
      },
      {
        icon: <ReceiptIcon className="viewLabelIcon" size={24} />,
        label: 'RealValue Commission',
        value: normalizeValue(PayoutViewData?.realvalueCommission, NOT_AVAILABLE)
      },
      {
        icon: <CalendarTodayIcon className="viewLabelIcon" size={24} />,
        label: 'Likely Payment Date',
        value: normalizeValue(PayoutViewData?.likelyPaymentDate, NOT_AVAILABLE)
      },
      {
        icon: <GiTakeMyMoney className="viewLabelIcon" size={24} />,
        label: 'Net Revenue',
        value: normalizeValue(PayoutViewData?.netRevenue, NOT_AVAILABLE)
      }
    ],
    [PayoutViewData]
  );

  // ✅ Payment Details fields (memoized)
  const paymentDetailsFields = useMemo(
    () => [
      {
        icon: <CalendarTodayIcon className="viewLabelIcon" size={24} />,
        label: 'Payment Date',
        value: normalizeValue(PayoutViewData?.paymentDate, NOT_AVAILABLE)
      },
      {
        icon: <GiMoneyStack className="viewLabelIcon" size={24} />,
        label: 'Gross Amount',
        value: normalizeValue(PayoutViewData?.grossAmount, NOT_AVAILABLE)
      },
      {
        icon: <GiPayMoney className="viewLabelIcon" size={24} />,
        label: 'Amount Paid',
        value: normalizeValue(PayoutViewData?.amountPaid, NOT_AVAILABLE)
      },
      {
        icon: <TbReceiptTax className="viewLabelIcon" size={24} />,
        label: 'TDS Deduction',
        value: normalizeValue(PayoutViewData?.tdsDeduction, NOT_AVAILABLE)
      },
      {
        icon: <HiReceiptTax className="viewLabelIcon" size={24} />,
        label: 'GST Paid Amount',
        value: normalizeValue(PayoutViewData?.gstPaidAmount, NOT_AVAILABLE)
      },
      {
        icon: <AddCardIcon className="viewLabelIcon" size={24} />,
        label: 'Payment Mode',
        value: normalizeValue(PayoutViewData?.paymentMode, NOT_AVAILABLE)
      }
    ],
    [PayoutViewData]
  );

  const payoutFields = useMemo(
    () => [
      {
        label: 'Application ID',
        value: PayoutViewData?.applicationId,
        icon: <EventNoteIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'File Number',
        value: PayoutViewData?.fileNumber,
        icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      },
      {
        label: 'Submission Date',
        value: PayoutViewData?.fileSubmissionDate ? fDate(PayoutViewData.fileSubmissionDate) : '',
        icon: <EventNoteIcon className="viewLabelIcon" size={24} />
      },

      {
        label: 'Bank',
        value: PayoutViewData?.bankName,
        icon: <EventNoteIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'City',
        value: PayoutViewData?.cityName,
        icon: <CalendarTodayIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Borrower Name',
        value: PayoutViewData?.borrowerName,
        icon: <FaRegUser className="viewLabelIcon" size={24} />
      },
      {
        label: 'Loan Type',
        value: PayoutViewData?.loanName,
        icon: <GroupIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Disbursed Date',
        value: PayoutViewData?.disburseDate || PayoutViewData?.disburseMonth,
        icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      },
      {
        label: 'Disbursed Amount',
        value: PayoutViewData?.disburseAmount,
        icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      },
      {
        label: 'Source Flag',
        value: PayoutViewData?.sourceFlag,
        icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      },
      {
        label: 'Partner',
        value: PayoutViewData?.partnerName,
        icon: <EventNoteIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Current Status',
        value: PayoutViewData?.currentStatus,
        icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      },
      {
        label: 'Last Updated',
        value: PayoutViewData?.fileLastUpdated ? fDate(PayoutViewData.fileLastUpdated) : '',
        icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      }

      // {
      //   label: 'Loan Account Number',
      //   value: PayoutViewData?.fileNumber,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },

      // {
      //   label: 'Sanctioned Amount',
      //   value: PayoutViewData?.sanctionedAmount,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },
      // {
      //   label: 'SM Contact Name',
      //   value: PayoutViewData?.smContactName,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },
      // {
      //   label: 'SM Mobile Number',
      //   value: PayoutViewData?.smMobileNumber,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },
      // {
      //   label: 'SM Email',
      //   value: PayoutViewData?.smEmail,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },

      // {
      //   label: 'Company Name',
      //   value: PayoutViewData?.companyName,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },
      // {
      //   label: 'DSA Name',
      //   value: PayoutViewData?.dsaName,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },

      // {
      //   label: 'Source Type',
      //   value: PayoutViewData?.sourceType,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },
      // {
      //   label: 'Current Stage',
      //   value: PayoutViewData?.currentStage,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // },

      // {
      //   label: 'Login Profile Id',
      //   value: PayoutViewData?.loginProfileId,
      //   icon: <MdOutlineDateRange className="viewLabelIcon" size={24} />
      // }
    ],
    [PayoutViewData]
  );
  const handlePendingStatus = async (data, status) => {
    const credentials = {
      partnerAdvanceId: data?.partnerAdvanceId,
      userId: LoginData?.userId,
      newStatus: status,
      remarks: 'Done'
    };
    const result = await dispatch(saveApiData(PayoutApi?.advancePendingStatus, credentials));
    if (result?.approvalStatus === 'REJECTED') {
      dispatch(setApiSuccess('Advance File Rejected'));
      getPayoutViewApi();
    } else if (result?.approvalStatus === 'APPROVED') {
      dispatch(setApiSuccess('Advance File Approved'));
      getPayoutViewApi();
    }
    // console.log('result222', result);
  };

  return (
    <Page title="Partner Management System || Payout">
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 8, pt: 2.5 }}>
        <Container maxWidth="xl">
          {/* Header Row */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            sx={{ mb: 3.5 }}
          >
            {/* Left: Back Button + Title + Subtitle */}
            <Stack spacing={0.5}>
              <Button
                onClick={() => navigate('/payout')}
                startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                sx={{
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: '13px',
                  p: 0,
                  minWidth: 0,
                  mb: 0.5,
                  textTransform: 'none',
                  '&:hover': { color: '#0f172a', bgcolor: 'transparent' }
                }}
              >
                Back to Payout List
              </Button>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: '#0f172a',
                  letterSpacing: '-0.5px'
                }}
              >
                Payout Details
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px', mt: 0.25 }}>
                View complete payout file details, commission breakdowns, and advance requests.
              </Typography>
            </Stack>

            {/* Right: Breadcrumbs & Current Status */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ fontSize: '13px', color: '#64748b' }}>
                <BrudCrumbs />
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                  Current Status:
                </Typography>
                <Box
                  sx={{
                    bgcolor: '#fef3c7',
                    color: '#b45309',
                    px: 1.75,
                    py: 0.4,
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.2px',
                    border: '1px solid #fde68a'
                  }}
                >
                  {PayoutViewData?.payoutStatus || NOT_AVAILABLE}
                </Box>
              </Stack>
            </Stack>
          </Stack>

          <Grid container spacing={3} mb={3}>
            <ViewCardComponent
              title="File Details"
              fields={payoutFields || []}
              handleEdit={handleEditOpen}
            />
            <ViewCardComponent title="Commission Details" fields={commissionDetailsFields} />

            {/* Advance Table Section */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                  p: { xs: 2.5, md: 3 },
                  bgcolor: '#ffffff'
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2.5, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '8px',
                        bgcolor: '#0284c7',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <AccountBalanceWalletIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                      Advance Details
                    </Typography>
                  </Stack>
                </Stack>

                <TableContainer
                  sx={{
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden'
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Partner Id</StyledTableCell>
                        <StyledTableCell>Payout Id</StyledTableCell>
                        <StyledTableCell>Request Amount</StyledTableCell>
                        <StyledTableCell>Approval Status</StyledTableCell>
                        <StyledTableCell>Purpose</StyledTableCell>
                        <StyledTableCell>Request Date</StyledTableCell>
                        <StyledTableCell align="center">Action</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {AdvancePendingViewData && AdvancePendingViewData?.length > 0 ? (
                        AdvancePendingViewData.map((item, index) => (
                          <StyledTableRow key={index}>
                            <StyledTableCell sx={{ fontWeight: 600, color: '#0284c7' }}>
                              {item?.partnerId ? item?.partnerId : `${NOT_AVAILABLE}`}
                            </StyledTableCell>
                            <StyledTableCell sx={{ fontWeight: 600, color: '#0284c7' }}>
                              {item.payoutId ? item.payoutId : `${NOT_AVAILABLE}`}
                            </StyledTableCell>
                            <StyledTableCell>
                              {item.requestAmount ? item.requestAmount : `${NOT_AVAILABLE}`}
                            </StyledTableCell>
                            <StyledTableCell>
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1.5,
                                  py: 0.35,
                                  borderRadius: '16px',
                                  fontSize: '12px',
                                  fontWeight: 700,
                                  ...(item.approvalStatus === 'APPROVED'
                                    ? { bgcolor: '#dcfce7', color: '#16a34a' }
                                    : item.approvalStatus === 'REJECTED'
                                    ? { bgcolor: '#fee2e2', color: '#dc2626' }
                                    : { bgcolor: '#fef3c7', color: '#d97706' })
                                }}
                              >
                                {item.approvalStatus ? item.approvalStatus : `${NOT_AVAILABLE}`}
                              </Box>
                            </StyledTableCell>
                            <StyledTableCell>
                              {item.purpose ? item.purpose : `${NOT_AVAILABLE}`}
                            </StyledTableCell>
                            <StyledTableCell>
                              {item.requestDate ? item.requestDate : `${NOT_AVAILABLE}`}
                            </StyledTableCell>
                            <StyledTableCell align="center" width={80}>
                              <UserMoreMenu
                                statusCode={handlePendingStatus}
                                rowId={item}
                                parent="advance-pending"
                              />
                            </StyledTableCell>
                          </StyledTableRow>
                        ))
                      ) : (
                        <TableRow>
                          <StyledTableCell
                            colSpan={7}
                            align="center"
                            sx={{ py: 3, color: '#94a3b8' }}
                          >
                            No Advance Found
                          </StyledTableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>

            {/* Queries Section */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                  p: { xs: 2.5, md: 3 },
                  bgcolor: '#ffffff'
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2.5, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '8px',
                        bgcolor: '#0284c7',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <QuestionAnswerIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                      Queries & Comments
                    </Typography>
                  </Stack>

                  <Tooltip title="Send Message" placement="top" arrow>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<MdOutlineSendToMobile size={16} />}
                      onClick={() => setOpenSendMsg(true)}
                      sx={{
                        bgcolor: '#0284c7',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        fontSize: '13px',
                        px: 2,
                        py: 0.6,
                        '&:hover': { bgcolor: '#0369a1' }
                      }}
                    >
                      Add Query
                    </Button>
                  </Tooltip>
                </Stack>

                <TableContainer
                  sx={{
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden'
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell sx={{ width: 180 }}>Date</StyledTableCell>
                        <StyledTableCell sx={{ width: 220 }}>Person Name</StyledTableCell>
                        <StyledTableCell>Comments</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {PayoutCommentLog && PayoutCommentLog?.length > 0 ? (
                        PayoutCommentLog.map((item, index) => {
                          let commentDetailsStr = item.commentDetails;
                          if (commentDetailsStr && commentDetailsStr.length > 40) {
                            commentDetailsStr = `${commentDetailsStr.substring(0, 40)}...`;
                          }
                          return (
                            <StyledTableRow key={index}>
                              <StyledTableCell sx={{ fontWeight: 500 }}>
                                {item.commentLogDate && item.commentLogDate !== ''
                                  ? fDate(item.commentLogDate)
                                  : `${NOT_AVAILABLE}`}
                              </StyledTableCell>
                              <StyledTableCell sx={{ fontWeight: 600, color: '#0284c7' }}>
                                {item.personName ? item.personName : `${NOT_AVAILABLE}`}
                              </StyledTableCell>
                              <StyledTableCell>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontSize: 13, color: '#475569' }}
                                  >
                                    {commentDetailsStr || `${NOT_AVAILABLE}`}
                                  </Typography>
                                  {item.commentDetails && item.commentDetails.length > 40 && (
                                    <Tooltip title="View Full Comment" placement="top" arrow>
                                      <IconButton
                                        size="small"
                                        sx={{
                                          color: '#0284c7',
                                          bgcolor: 'rgba(2, 132, 199, 0.08)',
                                          '&:hover': { bgcolor: 'rgba(2, 132, 199, 0.16)' },
                                          ml: 1
                                        }}
                                        onClick={() => {
                                          setViewMorePopup(true);
                                          setCompleteCommentMessage(item.commentDetails);
                                        }}
                                      >
                                        <MdOutlineMoreHoriz size={16} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </StyledTableCell>
                            </StyledTableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <StyledTableCell
                            colSpan={3}
                            align="center"
                            sx={{ py: 3, color: '#94a3b8' }}
                          >
                            No Queries Found
                          </StyledTableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>

            {/* Partner Payout Details (Invoice) */}
            {PayoutViewData?.invoiceStatus && PayoutViewData?.invoiceStatus !== 'NOT_GENERATED' && (
              <Grid item xs={12}>
                <Card
                  sx={{
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                    p: { xs: 2.5, md: 3 },
                    bgcolor: '#ffffff'
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 2.5, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '8px',
                          bgcolor: '#0284c7',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <ReceiptLongIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                        Partner Payout Details (Invoice)
                      </Typography>
                    </Stack>
                  </Stack>

                  {loadingInvoice ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                      <CircularProgress size={32} sx={{ color: '#0284c7' }} />
                    </Box>
                  ) : invoicePdfUrl ? (
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1.5, fontWeight: 600, color: '#64748b' }}
                      >
                        Invoice Tax Document
                      </Typography>
                      <Tooltip title="Click to view full Invoice PDF" placement="top" arrow>
                        <Box
                          onClick={() => setOpenPdfDialog(true)}
                          sx={{
                            width: '140px',
                            height: '170px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            bgcolor: '#f8fafc',
                            p: 1.5,
                            position: 'relative',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(2, 132, 199, 0.15)',
                              borderColor: '#0284c7'
                            }
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              zIndex: 10,
                              cursor: 'pointer'
                            }}
                          />
                          <iframe
                            src={invoicePdfUrl}
                            title="Invoice Preview"
                            style={{
                              width: '100%',
                              height: '120px',
                              border: 'none',
                              overflow: 'hidden'
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 1,
                              fontSize: '11px',
                              color: '#0284c7',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              zIndex: 11
                            }}
                          >
                            Tax Invoice
                          </Typography>
                        </Box>
                      </Tooltip>

                      {/* Confirm/Reject Buttons */}
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 2, width: '140px' }}
                        justifyContent="center"
                      >
                        {invoiceActionStatus === 'CONFIRMED' ? (
                          <Box
                            sx={{
                              color: '#16a34a',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            <FiCheck size={18} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Confirmed
                            </Typography>
                          </Box>
                        ) : invoiceActionStatus === 'REJECTED' ? (
                          <Box
                            sx={{
                              color: '#dc2626',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            <FiX size={18} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Rejected
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            <Tooltip title="Confirm Invoice" placement="top" arrow>
                              <IconButton
                                onClick={() => {
                                  setConfirmRejectAction('CONFIRM');
                                  setConfirmRejectDialog(true);
                                }}
                                sx={{
                                  bgcolor: 'rgba(22, 163, 74, 0.1)',
                                  color: '#16a34a',
                                  borderRadius: '8px',
                                  p: 1,
                                  '&:hover': { bgcolor: 'rgba(22, 163, 74, 0.2)' }
                                }}
                              >
                                <FiCheck size={16} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject Invoice" placement="top" arrow>
                              <IconButton
                                onClick={() => {
                                  setConfirmRejectAction('REJECT');
                                  setConfirmRejectDialog(true);
                                }}
                                sx={{
                                  bgcolor: 'rgba(220, 38, 38, 0.1)',
                                  color: '#dc2626',
                                  borderRadius: '8px',
                                  p: 1,
                                  '&:hover': { bgcolor: 'rgba(220, 38, 38, 0.2)' }
                                }}
                              >
                                <FiX size={16} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>

                      {/* Full Screen PDF Dialog */}
                      <Dialog
                        open={openPdfDialog}
                        onClose={() => setOpenPdfDialog(false)}
                        fullWidth
                        maxWidth="md"
                        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
                      >
                        <DialogTitle
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            bgcolor: '#f8fafc',
                            borderBottom: '1px solid #e2e8f0',
                            py: 2
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}
                          >
                            Tax Invoice Preview
                          </Typography>
                          <IconButton
                            onClick={() => setOpenPdfDialog(false)}
                            size="small"
                            sx={{ color: '#64748b' }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </DialogTitle>
                        <DialogContent dividers sx={{ p: 0, height: '70vh' }}>
                          <iframe
                            src={invoicePdfUrl}
                            width="100%"
                            height="100%"
                            title="Full Invoice PDF"
                            style={{ border: 'none' }}
                          />
                        </DialogContent>
                        <DialogActions
                          sx={{ p: 2, px: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}
                        >
                          <Button
                            onClick={() => setOpenPdfDialog(false)}
                            variant="contained"
                            sx={{
                              bgcolor: '#0284c7',
                              '&:hover': { bgcolor: '#0369a1' },
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: '8px',
                              px: 3
                            }}
                          >
                            Close
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </Box>
                  ) : (
                    <Box py={2} textAlign="center">
                      <Typography variant="body2" color="text.secondary">
                        No invoice PDF available
                      </Typography>
                    </Box>
                  )}
                </Card>
              </Grid>
            )}
          </Grid>

          {/* View More Popup */}
          <Dialog
            open={viewMorePopup}
            onClose={() => setViewMorePopup(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
          >
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                py: 2
              }}
            >
              <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                Comment Details
              </Typography>
              <IconButton
                onClick={() => setViewMorePopup(false)}
                size="small"
                sx={{ color: '#64748b' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
                {completeCommentMessage}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, px: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              <Button
                onClick={() => setViewMorePopup(false)}
                variant="contained"
                sx={{
                  bgcolor: '#0284c7',
                  '&:hover': { bgcolor: '#0369a1' },
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  px: 3
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit File Dialog */}
          <Dialog
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth={fullWidth}
            maxWidth={maxWidth}
            open={editFile}
            PaperProps={{
              sx: {
                borderRadius: '16px',
                overflow: 'visible',
                zIndex: 100000
              }
            }}
          >
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                py: 2
              }}
            >
              <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                Edit File
              </Typography>
              <IconButton onClick={() => setEditFile(false)} size="small" sx={{ color: '#64748b' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <form id="updatePayout" onSubmit={handleReduxSubmit(onSubmit)}>
                <Grid container spacing={3} sx={{ mt: 0.5, mb: 1 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="fileNumber"
                      label="File No"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="FILE_NO"
                      fieldType="TEXT_FIELD"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="fileSubmissionDate"
                      label="Submission Date"
                      type="date"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="DOB"
                      fieldType="DATE_FIELD"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                      name="bankId"
                      label="Bank Name"
                      control={control}
                      errors={errors}
                      options={BankDrop || []}
                      isRequired
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                      name="cityId"
                      label="City"
                      control={control}
                      errors={errors}
                      options={CityDrop || []}
                      isRequired
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="borrowerName"
                      label="Borrower Name"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="NAME"
                      fieldType="ALPHABET"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                      name="loanTypeId"
                      label="Loan Type"
                      control={control}
                      errors={errors}
                      options={LoanTypeDrop || []}
                      isRequired
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="disburseDate"
                      label="Disbursed Date"
                      type="date"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="DATE"
                      fieldType="DATE_FIELD"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="disburseAmount"
                      label="Disbursed Amount"
                      type="Number"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldType="AMOUNT_FIELD"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="sanctionedDate"
                      label="Sanctioned Date"
                      type="date"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="DATE"
                      fieldType="DATE_FIELD"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="sanctionedAmount"
                      label="Sanctioned Amount"
                      type="Number"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldType="AMOUNT_FIELD"
                    />
                  </Grid>
                  {/* Sanctioned Letter Doc Upload */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', mb: 0.8 }}
                      >
                        Sanctioned Letter Document
                      </Typography>
                      <Tooltip
                        title={
                          fileUploaded
                            ? `Uploaded: ${sanctionedFile?.name || 'File Selected'}`
                            : 'Click to upload PDF / JPG / PNG'
                        }
                        placement="top"
                        arrow
                      >
                        <Box
                          onClick={() => fileInputRef.current?.click()}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            border: fileUploaded ? '1px solid #16a34a' : '1px solid #e2e8f0',
                            borderRadius: '8px',
                            px: 2,
                            py: 1.1,
                            bgcolor: fileUploaded ? '#f0fdf4' : '#fff',
                            minHeight: '44px',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: fileUploaded ? '#16a34a' : '#0284c7'
                            }
                          }}
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            style={{ display: 'none' }}
                            onChange={handleSanctionedLetterUpload}
                          />
                          {uploadingFile ? (
                            <CircularProgress size={16} sx={{ color: '#0284c7' }} />
                          ) : fileUploaded ? (
                            <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 18 }} />
                          ) : (
                            <BiCloudUpload size={18} style={{ color: '#64748b' }} />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '13px',
                              color: fileUploaded ? '#16a34a' : '#64748b',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1
                            }}
                          >
                            {uploadingFile
                              ? 'Uploading...'
                              : fileUploaded
                              ? sanctionedFile?.name || 'File Selected'
                              : 'Upload document'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="encashmentDate"
                      label="Encashment Date"
                      type="date"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="DATE"
                      fieldType="DATE_FIELD"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="encashmentAmount"
                      label="Encashment Amount"
                      type="Number"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldType="AMOUNT_FIELD"
                    />
                  </Grid>
                </Grid>
              </form>
            </DialogContent>
            <DialogActions sx={{ p: 2, px: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              <Button
                onClick={() => setEditFile(false)}
                sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="updatePayout"
                variant="contained"
                sx={{
                  bgcolor: '#0284c7',
                  '&:hover': { bgcolor: '#0369a1' },
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  px: 3
                }}
              >
                Submit
              </Button>
            </DialogActions>
          </Dialog>

          {/* Confirm / Reject Dialog */}
          <Dialog
            open={confirmRejectDialog}
            onClose={() => setConfirmRejectDialog(false)}
            fullWidth
            maxWidth="xs"
            PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
          >
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                py: 2
              }}
            >
              <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                {confirmRejectAction === 'CONFIRM' ? 'Confirm Invoice' : 'Reject Invoice'}
              </Typography>
              <IconButton
                onClick={() => setConfirmRejectDialog(false)}
                size="small"
                sx={{ color: '#64748b' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Typography sx={{ fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
                Are you sure you want to{' '}
                <strong
                  style={{ color: confirmRejectAction === 'CONFIRM' ? '#16a34a' : '#dc2626' }}
                >
                  {confirmRejectAction === 'CONFIRM' ? 'confirm' : 'reject'}
                </strong>{' '}
                this invoice?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, px: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              <Button
                onClick={() => setConfirmRejectDialog(false)}
                sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmReject}
                variant="contained"
                sx={{
                  bgcolor: confirmRejectAction === 'CONFIRM' ? '#16a34a' : '#dc2626',
                  '&:hover': {
                    bgcolor: confirmRejectAction === 'CONFIRM' ? '#15803d' : '#b91c1c'
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  px: 3
                }}
              >
                {confirmRejectAction === 'CONFIRM' ? 'Confirm' : 'Reject'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add New Query Dialog */}
          <Dialog
            open={openSendMsg}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
          >
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                py: 2
              }}
            >
              <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                Add New Query
              </Typography>
              <IconButton
                onClick={() => {
                  setOpenSendMsg(false);
                  setAddNewComment('');
                }}
                size="small"
                sx={{ color: '#64748b' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Box sx={{ mt: 1 }}>
                <FormControl fullWidth>
                  <TextField
                    type="text"
                    variant="outlined"
                    placeholder="Write your query comments here..."
                    fullWidth
                    multiline
                    rows={4}
                    value={addNewComment}
                    onChange={(e) => setAddNewComment(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px'
                      }
                    }}
                  />
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, px: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              <Button
                onClick={() => {
                  setOpenSendMsg(false);
                  setAddNewComment('');
                }}
                sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                onClick={(e) => handleAddComment(e)}
                variant="contained"
                sx={{
                  bgcolor: '#0284c7',
                  '&:hover': { bgcolor: '#0369a1' },
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  px: 3
                }}
              >
                Submit
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </Page>
  );
}
export default ViewPayout;
